import { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { BoutonGoogle } from '../components/BoutonGoogle.jsx';

/**
 * Le serveur ne renvoie qu'un code : le message reste ici, en français, avec
 * la conduite à tenir. Un code inconnu ne doit pas laisser l'écran muet.
 */
const MESSAGES_ERREUR = {
  google_indisponible:
    'La connexion Google est momentanément indisponible. Réessayez, ou utilisez votre mot de passe.',
  etat_invalide:
    'La connexion Google a été interrompue ou a expiré. Relancez-la depuis cette page.',
  email_non_verifie:
    'Google n’a pas vérifié cette adresse email. Vérifiez-la chez Google, ou connectez-vous avec un mot de passe.',
  reponse_google_invalide:
    'Réponse inattendue de Google. Réessayez, ou utilisez votre mot de passe.',
  compte_desactive: 'Ce compte a été désactivé.',
};

export function Auth({ mode = 'login' }) {
  const inscription = mode === 'register';
  const { user, chargement, connexion, inscription: creerCompte } = useAuth();
  const emplacement = useLocation();
  const [parametres, definirParametres] = useSearchParams();

  // Erreur rapportée par la route de retour de Google, transmise dans l'URL.
  const codeErreurGoogle = parametres.get('erreur');
  const erreurGoogle = codeErreurGoogle
    ? MESSAGES_ERREUR[codeErreurGoogle] ?? 'La connexion Google a échoué. Réessayez.'
    : null;

  // Écran demandé avant d'être renvoyé ici par le garde de route, sinon le
  // tableau de bord. Une seule destination pour les deux cas : quand la
  // redirection après connexion et celle du « déjà connecté » divergent, la
  // seconde gagne et l'utilisateur perd l'écran qu'il visait.
  const destination = emplacement.state?.depuis ?? '/voyages';

  const [champs, setChamps] = useState({ email: '', password: '', firstName: '' });
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  if (chargement) {
    return (
      <main className="page">
        <p role="status">Chargement…</p>
      </main>
    );
  }

  // Déjà connecté : inutile d'afficher le formulaire. C'est aussi ce qui
  // redirige juste après une connexion réussie, l'état `user` changeant.
  if (user) return <Navigate to={destination} replace />;

  const modifier = (champ) => (evenement) => {
    setChamps((precedent) => ({ ...precedent, [champ]: evenement.target.value }));
  };

  async function soumettre(evenement) {
    evenement.preventDefault();
    setErreur(null);
    // L'erreur Google vient de l'URL : sans cela, elle resterait affichée
    // pendant une tentative de connexion par mot de passe.
    if (codeErreurGoogle) definirParametres({}, { replace: true });
    setEnvoi(true);

    try {
      if (inscription) {
        await creerCompte(champs);
      } else {
        await connexion({ email: champs.email, password: champs.password });
      }
      // La redirection est assurée par la garde ci-dessus, dès que `user` est
      // renseigné : rien à faire de plus ici.
    } catch (echec) {
      setErreur(echec.message);
      setEnvoi(false);
    }
  }

  return (
    <main className="page page--etroite">
      <section className="verre">
        <h1>{inscription ? 'Créer un compte' : 'Se connecter'}</h1>

        <form onSubmit={soumettre} noValidate>
          {inscription && (
            <div className="champ">
              <label htmlFor="firstName">Prénom</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={champs.firstName}
                onChange={modifier('firstName')}
                required
              />
            </div>
          )}

          <div className="champ">
            <label htmlFor="email">Adresse email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={champs.email}
              onChange={modifier('email')}
              required
            />
          </div>

          <div className="champ">
            <label htmlFor="password">Mot de passe</label>
            <input
              id="password"
              name="password"
              type="password"
              // Indique au gestionnaire de mots de passe s'il doit proposer une
              // valeur existante ou en générer une nouvelle.
              autoComplete={inscription ? 'new-password' : 'current-password'}
              value={champs.password}
              onChange={modifier('password')}
              required
            />
            {inscription ? (
              <p className="aide">Au moins 10 caractères.</p>
            ) : (
              <p className="aide">
                <Link to="/mot-de-passe-oublie">Mot de passe oublié ?</Link>
              </p>
            )}
          </div>

          {/* `role="alert"` fait annoncer le message par les lecteurs d'écran. */}
          {(erreur || erreurGoogle) && (
            <p className="erreur" role="alert">
              {erreur ?? erreurGoogle}
            </p>
          )}

          <button className="bouton" type="submit" disabled={envoi}>
            {envoi ? 'Envoi…' : inscription ? 'Créer mon compte' : 'Se connecter'}
          </button>
        </form>

        <div className="separateur-ou">
          <span>ou</span>
        </div>

        <BoutonGoogle
          libelle={inscription ? 'S’inscrire avec Google' : 'Continuer avec Google'}
        />

        <p className="bascule">
          {inscription ? (
            <>
              Déjà un compte ? <Link to="/connexion">Se connecter</Link>
            </>
          ) : (
            <>
              Pas encore de compte ? <Link to="/inscription">Créer un compte</Link>
            </>
          )}
        </p>
      </section>
    </main>
  );
}
